import { Link } from "react-router-dom";
import { ShieldCheck, FileText, Sparkles, Users, PhoneCall, Building2, ArrowRight, CheckCircle2 } from "lucide-react";
import { useSite } from "../../lib/useSite";
import { Reveal, Eyebrow } from "../../components/bits";

const HERO = "https://images.pexels.com/photos/33685861/pexels-photo-33685861.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const RES = "https://images.unsplash.com/photo-1663811397207-418a92396ad5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBpbnRlcmlvciUyMG1vZGVybnxlbnwwfHx8fDE3OTAxMjA4MzB8MA&ixlib=rb-4.1.0&q=85";
const COM = "https://images.pexels.com/photos/22711217/pexels-photo-22711217.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const TRUST = [
  [ShieldCheck, "Professional service", "Trained, carefully managed cleaners delivering consistent, premium results."],
  [FileText, "Clear written quotations", "Transparent pricing with no surprises — indicative estimates and formal quotes."],
  [Sparkles, "Quality control", "Post-clean checks and supervisor review keep standards diamond-sharp."],
  [Users, "Carefully managed cleaners", "Individual compliance is tracked internally per worker for accountability."],
  [PhoneCall, "Reliable communication", "Responsive updates and a dedicated reception assistant, Rachel."],
  [Building2, "Residential & commercial", "From homes to offices and landlord turnarounds across London."],
];

export default function Home() {
  const site = useSite();
  const brand = site?.brand || {};
  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[86vh] flex items-center">
        <img src={HERO} alt="Luxury interior" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0a0a0a]/70" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full">
          <div>
            <Reveal>
              <Eyebrow dark>North London · NW London · Watford</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="font-display text-[#fdfbf7] text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-3xl">
                Affordable Cleaning.<br /><span className="text-[#d4af37]">Diamond Standards.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 text-[#cfcabf] text-base sm:text-lg max-w-xl leading-relaxed">
                {brand.hero_sub || "Professional residential and commercial cleaning with premium standards, dependable service and carefully managed customer care."}
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <Link to="/quote" data-testid="hero-quote-btn" className="btn-gold px-8 py-4 rounded-sm text-center inline-flex items-center justify-center gap-2">
                  Get My Quote <ArrowRight size={18} />
                </Link>
                <Link to="/book" data-testid="hero-book-btn" className="btn-outline-gold px-8 py-4 rounded-sm text-center">
                  Book a Clean
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <Reveal><Eyebrow>Why Black Diamond</Eyebrow></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight max-w-2xl leading-tight">
            Professional. Reliable. Discreet. Premium. Organised.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
          {TRUST.map(([Icon, title, body], i) => (
            <Reveal key={title} delay={i * 0.05}>
              <div className="card-luxe p-8 h-full">
                <Icon className="text-[#d4af37]" size={26} />
                <h3 className="font-display text-2xl mt-5 tracking-tight">{title}</h3>
                <p className="text-[#555] text-sm mt-3 leading-relaxed">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 text-xs text-[#8a8a8a] max-w-2xl leading-relaxed">
            We track individual worker compliance (DBS, right-to-work and insurance) internally and only present a status
            once verified. We do not make blanket claims that are not confirmed.
          </p>
        </Reveal>
      </section>

      {/* INSURANCE (only when cover active) */}
      {site?.insurance?.active && (
        <section className="bg-[#1c1c1c]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
            <div className="flex items-center gap-3 mb-6"><span className="w-8 h-px bg-[#d4af37]" /><span className="text-[11px] tracking-luxe uppercase text-[#d4af37]">Insured & Protected</span></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {site.insurance.claims.map((c) => (
                <div key={c} className="flex items-center gap-2 text-[#e8e5de] text-sm border border-[#333] rounded-sm px-4 py-3">
                  <CheckCircle2 size={16} className="text-[#d4af37] shrink-0" /> {c}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RES / COMMERCIAL split */}
      <section className="bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[[RES, "Residential", "Regular, one-off, deep and end-of-tenancy cleaning for homes that deserve immaculate care.", "/services"],
            [COM, "Commercial", "Discreet, reliable office and commercial cleaning with site-specific schedules and SLAs.", "/commercial"]].map(([img, label, body, link], i) => (
            <Reveal key={label} delay={i * 0.1}>
              <Link to={link} className="group block relative h-[380px] overflow-hidden rounded-sm border border-[#333]">
                <img src={img} alt={label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/30 to-transparent" />
                <div className="absolute bottom-0 p-8">
                  <p className="text-[11px] tracking-luxe text-[#d4af37] uppercase">{label}</p>
                  <h3 className="font-display text-3xl text-[#fdfbf7] mt-2">{label} Cleaning</h3>
                  <p className="text-[#cfcabf] text-sm mt-2 max-w-md">{body}</p>
                  <span className="inline-flex items-center gap-2 text-[#d4af37] text-sm mt-4">Explore <ArrowRight size={16} /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <Reveal><Eyebrow>How it works</Eyebrow></Reveal>
        <Reveal delay={0.05}><h2 className="font-display text-4xl sm:text-5xl tracking-tight">A seamless, managed experience</h2></Reveal>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mt-14">
          {[["01", "Request a quote", "Tell us about your property and needs."],
            ["02", "Written quotation", "Receive a clear indicative estimate or formal quote."],
            ["03", "Book & assign", "We schedule and assign a carefully managed cleaner."],
            ["04", "Clean & review", "Quality-checked clean with feedback and invoicing."]].map(([n, t, b], i) => (
            <Reveal key={n} delay={i * 0.06}>
              <div className="border-t border-[#e0ddd4] pt-6">
                <span className="font-display text-4xl text-[#d4af37]">{n}</span>
                <h3 className="text-lg font-semibold mt-3">{t}</h3>
                <p className="text-[#555] text-sm mt-2 leading-relaxed">{b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#1c1c1c]">
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 py-20 sm:py-24 text-center">
          <span className="diamond mx-auto" />
          <h2 className="font-display text-4xl sm:text-5xl text-[#fdfbf7] mt-6 tracking-tight">Ready for diamond standards?</h2>
          <p className="text-[#cfcabf] mt-4 max-w-xl mx-auto">Request your free, no-obligation quotation today. Our reception assistant Rachel is on hand 09:00–17:00.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote" className="btn-gold px-8 py-4 rounded-sm inline-flex items-center justify-center gap-2">Get My Quote <ArrowRight size={18} /></Link>
            <Link to="/contact" className="btn-outline-gold px-8 py-4 rounded-sm">Contact Us</Link>
          </div>
          <ul className="mt-10 flex flex-wrap gap-x-8 gap-y-2 justify-center text-[#cfcabf] text-sm">
            {["No obligation", "Clear written quotes", "Carefully managed cleaners", "Residential & commercial"].map((t) => (
              <li key={t} className="flex items-center gap-2"><CheckCircle2 size={15} className="text-[#d4af37]" /> {t}</li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
